/**
 * Pitch — the interactive canvas. Drives AIMING → COMPUTING → REPLAYING →
 * RESULT via gameStore.
 *
 * v3 timing model (closes audit P1-1):
 *   1. User commits a kick → state goes AIMING → COMPUTING.
 *      Ball is locked at rest; canvas shows a "syncing keeper" pip after
 *      ~600ms so slow-network users see we're working on it.
 *   2. POST /api/attempt fires.
 *      .then  → store.startReplayWithServerOutcome() flips state to
 *               REPLAYING with keeperReveal in hand. scene.replay is set
 *               here, scene.elapsed = 0. Ball + keeper share ONE clock.
 *      .catch → store.startReplayLocal() falls back to a ball-only replay
 *               using the local-sim trajectory; keeperReveal stays null.
 *   3. RAF loop accumulates `dt` (which RafLoop already clamps to 33ms).
 *      Tab backgrounding pauses the animation correctly because dt only
 *      ticks on visible frames.
 *   4. Replay ends when scene.elapsed >= replayDuration. State → RESULT.
 *
 * The local-sim is now used ONLY for two things: the live trajectory
 * preview while aiming, and as the ball-trajectory fallback when the API
 * fails. The server's outcome is canonical.
 */
import { useEffect, useRef } from "react";
import {
  PitchRenderer,
  RafLoop,
  attachAim,
  attachCanvas,
  installAudioUnlock,
  playGoal,
  playKick,
  playMiss,
  sampleTrajectory,
  simulate,
} from "@/game";
import type {
  AimInput,
  PublicPuzzleParams,
  PuzzleParams,
  SimResult,
  Vec3,
} from "@/game";
import { ApiError, api } from "@/api/client";
import { useGameStore } from "@/store/gameStore";

interface PitchProps {
  params: PublicPuzzleParams;
  puzzleId: string;
}

const PREVIEW_THROTTLE_MS = 33;
const POST_HIT_HOLD_SEC = 0.4;

/**
 * Construct a complete PuzzleParams from public-only data by adding an
 * inert keeper. Used ONLY for client-side trajectory preview + the
 * fallback replay when the API fails — the server runs simulate() with
 * the real keeper secrets to determine the canonical outcome.
 */
function withInertKeeper(p: PublicPuzzleParams): PuzzleParams {
  return {
    ...p,
    keeper: {
      restPos: p.keeper.restPos,
      diveTo: p.keeper.restPos,    // doesn't move
      reactionTime: 999,            // never reacts within sim window
      diveDuration: 1,
      reach: 0,                     // can't catch
    },
  };
}

function describeAttemptError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 408) return "network_timeout";
    const body = err.body;
    if (body && typeof body === "object" && "error" in body) {
      return String((body as { error: unknown }).error);
    }
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return "submit_failed";
}

export function Pitch({ params, puzzleId }: PitchProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // On mount (or whenever puzzleId changes), check if the user already
  // attempted today's puzzle. If so, restore the store to RESULT immediately
  // so the canvas locks and ResultOverlay surfaces the prior outcome — no
  // animation, no fresh local sim, no chance of the "wide kick + WALL text"
  // mismatch that launch-day testing caught.
  useEffect(() => {
    let cancelled = false;
    api
      .todayAttempt()
      .then((res) => {
        if (cancelled) return;
        if (!res.exists) return;
        if (res.puzzleId !== puzzleId) return; // puzzle rolled over mid-load
        useGameStore.getState().restoreExistingAttempt({
          attemptId: res.attemptId,
          outcome: res.outcome,
          outcomeDetail: res.outcomeDetail,
          score: res.score,
          shareCardText: res.shareCardText,
          streak: res.streak,
          keeperReveal: res.keeperReveal,
        });
      })
      .catch(() => {
        // Fresh user / 401 / transient network — fall through to AIMING.
        // The first POST /api/attempt will surface any real server error.
      });
    return () => { cancelled = true; };
  }, [puzzleId]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const view = attachCanvas(canvas);
    const renderer = new PitchRenderer(view, params);
    installAudioUnlock();

    // Inert-keeper version for local-only simulate() calls.
    const inertParams = withInertKeeper(params);

    const scene = {
      aim: null as AimInput | null,
      preview: null as Vec3[] | null,
      replay: null as { result: SimResult } | null,
      ball: { x: params.ball.x, y: params.ball.y, z: 0 } as Vec3,
      /** Accumulated dt seconds while in REPLAYING. Tab-backgrounding pauses
       *  this correctly because dt only ticks on visible frames. */
      elapsed: 0,
      replayDone: false,
    };

    let lastPreviewTs = 0;

    const aim = attachAim(canvas, {
      onAimPreview: (input) => {
        scene.aim = input;
        const now = performance.now();
        if (now - lastPreviewTs > PREVIEW_THROTTLE_MS) {
          scene.preview = simulate(input, inertParams).trajectory;
          lastPreviewTs = now;
        }
      },
      onAimCommit: (input) => {
        // Guard: only commit while the game is actually accepting input.
        // Without this, the canvas keeps accepting drags after the user's
        // one daily kick — each one POSTs /api/attempt and gets the cached
        // outcome back, while the canvas animates a fresh local trajectory.
        // That mismatch is exactly the "every kick says WALL even when I
        // kick wide" launch-day report.
        if (useGameStore.getState().state !== "AIMING") return;

        // Local sim — used for the ball trajectory in the replay (whether
        // the API succeeds or not — the server runs the same engine on
        // the same inputs, so trajectories agree byte-for-byte).
        const localResult = simulate(input, inertParams);
        playKick();

        scene.aim = null;
        scene.preview = null;

        // Lock the input. Canvas freezes ball at rest. State = COMPUTING.
        useGameStore.getState().beginCompute(input);

        const debug =
          typeof window !== "undefined" &&
          new URLSearchParams(window.location.search).get("debug") === "1";
        const submitStartMs = performance.now();

        api
          .submitAttempt({ puzzleId, input })
          .then((server) => {
            if (debug) {
              const elapsedMs = performance.now() - submitStartMs;
              // eslint-disable-next-line no-console
              console.info("[curvle/debug] /api/attempt response in", elapsedMs.toFixed(0) + "ms", {
                outcome: server.outcome,
                keeperReveal: server.keeperReveal,
                alreadyAttempted: server.alreadyAttempted,
              });
            }
            // If the server is returning the user's existing attempt for
            // today, skip the replay entirely. The local trajectory we just
            // computed corresponds to THIS submission's input, not the
            // user's original kick — animating it would contradict the
            // outcome text. Jump straight to RESULT with the stored data.
            if (server.alreadyAttempted) {
              useGameStore.getState().restoreExistingAttempt({
                attemptId: server.attemptId,
                outcome: server.outcome,
                outcomeDetail: server.outcomeDetail,
                score: server.score,
                shareCardText: server.shareCardText,
                streak: server.streak,
                keeperReveal: server.keeperReveal,
              });
              return;
            }
            // Single atomic transition: scene.replay set, elapsed reset,
            // store flipped to REPLAYING. Ball + keeper share one clock.
            scene.replay = { result: localResult };
            scene.elapsed = 0;
            scene.replayDone = false;
            useGameStore.getState().startReplayWithServerOutcome(server, localResult);
          })
          .catch((err: unknown) => {
            // Network / timeout / 5xx. Fall back to a ball-only replay so
            // the user still sees motion. No keeper data — drawKeeper will
            // render at restPos. Result panel shows the error.
            const errorCode = describeAttemptError(err);
            scene.replay = { result: localResult };
            scene.elapsed = 0;
            scene.replayDone = false;
            useGameStore.getState().startReplayLocal(input, localResult, errorCode);
            // eslint-disable-next-line no-console
            console.warn("[curvle] /api/attempt failed:", err);
          });
      },
      onCancel: () => {
        scene.aim = null;
        scene.preview = null;
      },
    });

    const ro = new ResizeObserver(() => renderer.resync());
    ro.observe(canvas);

    const loop = new RafLoop((dt, _ts) => {
      const gs = useGameStore.getState();

      if (gs.state === "AIMING") {
        scene.ball = { x: params.ball.x, y: params.ball.y, z: 0 };
        scene.elapsed = 0;
        renderer.draw({
          ball: scene.ball,
          replayTime: 0,
          phase: "AIMING",
          aim: scene.aim,
          preview: scene.preview,
          keeperReveal: null,
        });
        return;
      }

      if (gs.state === "COMPUTING") {
        // Ball frozen at rest. Don't accumulate elapsed yet — replay
        // hasn't started. Renderer treats this like AIMING (no preview).
        scene.ball = { x: params.ball.x, y: params.ball.y, z: 0 };
        renderer.draw({
          ball: scene.ball,
          replayTime: 0,
          phase: "AIMING",
          aim: null,
          preview: null,
          keeperReveal: null,
        });
        return;
      }

      if (gs.state === "REPLAYING" && scene.replay) {
        // Accumulate dt — RafLoop has already clamped each tick to 33ms,
        // so tab-backgrounding can't make this jump. Only visible frames
        // contribute. Ball + keeper share this single clock.
        scene.elapsed += dt;
        const elapsed = scene.elapsed;
        scene.ball = sampleTrajectory(scene.replay.result.trajectory, elapsed);

        renderer.draw({
          ball: scene.ball,
          replayTime: elapsed,
          phase: "REPLAYING",
          aim: null,
          preview: null,
          keeperReveal: gs.keeperReveal,
        });

        // Replay ends when ball is settled AND keeper has finished its
        // dive (if reveal is present). With the new single-clock design
        // these are both measured in the same accumulated-dt seconds.
        const reveal = gs.keeperReveal;
        const ballMin = scene.replay.result.hitTime + POST_HIT_HOLD_SEC;
        const keeperMin = reveal
          ? reveal.reactionTime + reveal.diveDuration + 0.3
          : 0;
        const replayDuration = Math.max(ballMin, keeperMin);

        if (!scene.replayDone && elapsed >= replayDuration) {
          scene.replayDone = true;
          const finalOutcome = useGameStore.getState().outcome ?? scene.replay.result.outcome;
          if (finalOutcome === "GOAL") playGoal();
          else playMiss();
          useGameStore.getState().finishReplay();
        }
        return;
      }

      // RESULT — hold the last frame so ResultOverlay overlays it.
      renderer.draw({
        ball: scene.ball,
        replayTime: scene.elapsed,
        phase: gs.state === "RESULT" ? "RESULT" : "LOADING",
        aim: null,
        preview: null,
        keeperReveal: gs.keeperReveal,
      });
    });

    loop.start();

    return () => {
      loop.stop();
      ro.disconnect();
      aim.dispose();
      view.dispose();
    };
  }, [params, puzzleId]);

  return <canvas ref={canvasRef} className="pitch" aria-label="Free-kick pitch" />;
}
